import React, { type ReactNode } from 'react';
import RenderStars from '../../Components/RenderStars';
import type { Restaurant } from '../../../Manager/restaurant.types';

interface RecentViewedProps {
  renderStars: (rating: number) => ReactNode;
  loading?: boolean;
  restaurants?: Restaurant[];
  onCardClick: (id: string, name: string) => void;
}

const RecentViewed: React.FC<RecentViewedProps> = ({ loading, restaurants, onCardClick }) => {
  if (loading) {
    return (
      <div className="mb-2">
        <h2 className="text-lg ml-2 pb-2 font-bold poppins-semibold text-[var(--title-primary)]">Recently viewed</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {[...Array(6)].map((_, idx) => (
            <div key={idx} className="flex gap-3 bg-white rounded-xl overflow-hidden shadow-sm border border-lg border-gray-100">
              <div className="w-24 h-24 bg-gray-200 rounded-md" />
              <div className="flex-1 py-3 pr-3 min-w-0">
                <div className="h-4 bg-gray-200 rounded w-full mb-1" />
                <div className="h-4 bg-gray-200 rounded w-full mb-1" />
                <div className="h-4 bg-gray-200 rounded w-full" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!loading && !restaurants?.length) return null;

  return (
    <div className="mb-2">
      <h2 className="text-lg ml-2 pb-2 font-bold poppins-semibold text-[var(--title-primary)]">Recently viewed</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {restaurants.map((place, idx) => {
          const metadataRating =
            typeof place.googleMetadata?.rating === 'number' ? place.googleMetadata.rating : null;
          const overallRating = metadataRating ?? place.rating.average ?? 0;
          const cuisineSummary =
            place?.cuisine?.slice(0, 2).join(' \u2022 ') || place?.typeOfFood?.slice(0, 2).join(' \u2022 ');

          return (
            <div
              key={idx}
              className="flex gap-3 bg-white rounded-xl overflow-hidden shadow-sm border border-lg border-gray-100"
              onClick={() => onCardClick(place._id, place.name)}
            >
              <img
                src={place.gallery.cover.url}
                alt={place.name}
                className="w-24 h-24 object-cover flex-shrink-0"
              />
              <div className="flex-1 py-3 pr-3 min-w-0">
                <h3 className="text-sm font-semibold poppins-semibold text-gray-800 mb-1">{place.name}</h3>
                <div className="flex items-center mb-1">
                  <span className="text-sm font-semibold text-gray-900 mr-2">{overallRating.toFixed(1)}</span>
                  <RenderStars rating={overallRating} size={12} />
                </div>
                <p className="text-sm text-gray-600 truncate max-w-full">
                  From KSh {place.priceRange.min} {'\u2022'} {cuisineSummary}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RecentViewed;
